// Histórico do desconto da UC — tabela `uc_desconto_historico` (migration
// 20261009132443). Dois tipos de registro:
//   'uc'     → o cadastro da UC mudou e vale para as próximas faturas. Gravado pelo
//              gatilho do banco, então pega qualquer tela (e até SQL direto).
//   'fatura' → desconto diferente só numa fatura; o cadastro ficou como estava.
//
// Caso Aline Medeiros Elias (UC 420455101230, 09/10/2026): o boleto de SET saiu com
// 30%, em OUT a UC estava de volta em 20%, e ninguém sabia quem voltou nem se os 30%
// eram só daquele mês — o cálculo da fatura copiava o desconto para a UC sem perguntar.
import type { SupabaseClient } from '@supabase/supabase-js';
import { fracaoParaPercentTexto } from './descontoPercentual.ts';

export type EscopoDesconto = 'uc' | 'fatura';

/** Fração (0.2 = 20%). O PostgREST devolve `numeric` como string. */
type Fracao = number | string | null | undefined;

export interface Descontos {
  desconto_tarifa: Fracao;
  desconto_bandeira: Fracao;
}

export interface RegistroDesconto {
  id: number;
  unidade_consumidora_id: number | null;
  fatura_id: number | null;
  escopo: EscopoDesconto;
  desconto_tarifa_antes: Fracao;
  desconto_tarifa_depois: Fracao;
  desconto_bandeira_antes: Fracao;
  desconto_bandeira_depois: Fracao;
  motivo: string | null;
  origem: string | null;
  alterado_por_nome: string | null;
  alterado_em: string;
  fatura?: { mes_referencia: string | null } | null;
}

/** Em centésimos de ponto percentual (0,01%), o mesmo passo de lerPercentDigitado. */
const emCentesimos = (v: Fracao): number | null => {
  if (v === null || v === undefined || v === '' || !Number.isFinite(Number(v))) return null;
  return Math.round(Number(v) * 10000);
};

export function mesmoDesconto(a: Descontos, b: Descontos): boolean {
  return emCentesimos(a.desconto_tarifa) === emCentesimos(b.desconto_tarifa)
    && emCentesimos(a.desconto_bandeira) === emCentesimos(b.desconto_bandeira);
}

const depoisDe = (r: RegistroDesconto): Descontos => ({
  desconto_tarifa: r.desconto_tarifa_depois,
  desconto_bandeira: r.desconto_bandeira_depois,
});

/**
 * A fatura vai sair com desconto diferente do cadastro, e ninguém disse ainda se é
 * só nela ou daqui pra frente. Vale como decidido o último registro DESTA fatura que
 * termina no desconto que ela tem agora.
 */
export function precisaDecidirAlcance(
  fatura: Descontos,
  cadastro: Descontos,
  registros: RegistroDesconto[],
  faturaId: number | string,
): boolean {
  if (mesmoDesconto(fatura, cadastro)) return false;
  const ultimo = registros
    .filter(r => r.fatura_id != null && String(r.fatura_id) === String(faturaId))
    .sort((a, b) => b.alterado_em.localeCompare(a.alterado_em))[0];
  return !(ultimo && mesmoDesconto(depoisDe(ultimo), fatura));
}

export const percentTexto = (v: Fracao): string => {
  const c = emCentesimos(v);
  return c === null ? '—' : `${fracaoParaPercentTexto(c / 10000)}%`;
};

/** "Tarifa 20% → 30%" — bandeira só entra se mudou. Sem "antes" é o cadastro inicial. */
export function descreverMudanca(r: RegistroDesconto): string {
  const parte = (nome: string, antes: Fracao, depois: Fracao) => {
    if (emCentesimos(antes) === null) return `${nome} ${percentTexto(depois)}`;
    return `${nome} ${percentTexto(antes)} → ${percentTexto(depois)}`;
  };
  const tarifaMudou = emCentesimos(r.desconto_tarifa_antes) !== emCentesimos(r.desconto_tarifa_depois);
  const bandeiraMudou = emCentesimos(r.desconto_bandeira_antes) !== emCentesimos(r.desconto_bandeira_depois);
  const partes: string[] = [];
  if (tarifaMudou || !bandeiraMudou) partes.push(parte('Tarifa', r.desconto_tarifa_antes, r.desconto_tarifa_depois));
  if (bandeiraMudou) partes.push(parte('Bandeira', r.desconto_bandeira_antes, r.desconto_bandeira_depois));
  return partes.join(' · ');
}

export function rotuloAlcance(r: RegistroDesconto): string {
  if (r.escopo === 'fatura') {
    const mes = r.fatura?.mes_referencia;
    return mes ? `Só na fatura de ${mes}` : 'Só numa fatura';
  }
  if (emCentesimos(r.desconto_tarifa_antes) === null && emCentesimos(r.desconto_bandeira_antes) === null) {
    return 'Cadastro inicial da UC';
  }
  return 'Cadastro da UC (vale daqui pra frente)';
}

const ORIGENS: Record<string, string> = {
  calculo_fatura: 'cálculo da fatura',
  cadastro_uc: 'cadastro da UC',
};

export const rotuloOrigem = (origem: string | null): string | null =>
  origem ? (ORIGENS[origem] ?? origem) : null;

export async function buscarHistoricoDesconto(
  supabase: SupabaseClient,
  ucId: number | string,
  limite = 50,
): Promise<RegistroDesconto[]> {
  const { data, error } = await supabase
    .from('uc_desconto_historico')
    .select('id, unidade_consumidora_id, fatura_id, escopo, desconto_tarifa_antes, desconto_tarifa_depois, desconto_bandeira_antes, desconto_bandeira_depois, motivo, origem, alterado_por_nome, alterado_em, fatura:faturas(mes_referencia)')
    .eq('unidade_consumidora_id', ucId)
    .order('alterado_em', { ascending: false })
    .limit(limite);
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as RegistroDesconto[];
}

export async function registrarDescontoDaFatura(
  supabase: SupabaseClient,
  p: {
    faturaId: number | string;
    ucId: number | string;
    escopo: EscopoDesconto;
    novo: Descontos;
    antes: Descontos;
    motivo: string;
  },
): Promise<void> {
  const num = (v: Fracao) => (emCentesimos(v) === null ? null : Number(v));
  const { error } = await supabase.rpc('registrar_desconto_da_fatura', {
    p_fatura_id: Number(p.faturaId),
    p_uc_id: Number(p.ucId),
    p_escopo: p.escopo,
    p_desconto_tarifa: num(p.novo.desconto_tarifa),
    p_desconto_bandeira: num(p.novo.desconto_bandeira),
    p_desconto_tarifa_antes: num(p.antes.desconto_tarifa),
    p_desconto_bandeira_antes: num(p.antes.desconto_bandeira),
    p_motivo: p.motivo,
  });
  if (error) throw new Error(error.message);
}
